import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'nurse' | 'admin' | ('nurse' | 'admin')[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole
}) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

if (!isAuthenticated) {
  return <Navigate to="/login" replace />;
}


  // Role-based access restriction
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    // If we don't know the role yet, wait for auth state to settle
    if (!user?.role) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh'
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!allowedRoles.includes(user.role as 'nurse' | 'admin')) {
      // Send mismatched roles to their own dashboard instead of looping
      const redirectPath = user.role === 'admin' ? '/admin' : '/dashboard';
      if (redirectPath === window.location.pathname) {
        return <Navigate to="/home" replace />;
      }
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
